import { isRef, watch, onMounted, onUnmounted, reactive } from 'vue'
import intlTelInput from 'intl-tel-input'
import 'intl-tel-input/styles'
import { useKeycloakStore } from '../pinia-stores/keycloak.js'
import { useAccountStore } from '../pinia-stores/account.js'
import { useContactFill } from './useContactFill.js'
import { useCountryPicker } from './useCountryPicker.js'

export function useTelephone(inputElement = null, options = {}) {
  // Default the initial country to the user's country. Profile/claim data can
  // hold 3-letter codes (e.g. "CIV"): intl-tel-input needs a 2-letter iso2 or
  // it selects NO country — which hides the flag, dial code and dropdown.
  const keycloakStore = useKeycloakStore()
  const accountStore = useAccountStore()
  const {
    contactsAvailable,
    fillFromContacts,
    attachContactButton,
  } = useContactFill()
  const validIso2 = (code) => {
    const iso2 = String(code || '').trim().toLowerCase()
    if (/^[a-z]{2}$/.test(iso2)) return iso2
    if (code) console.warn('[useTelephone] ignoring invalid country code:', code)
    return ''
  }
  const userCountry =
    validIso2(keycloakStore?.profile?.countryCode) ||
    validIso2(keycloakStore?.userInfo?.countryCode) ||
    validIso2(accountStore?.userAccount?.countryCode) ||
    'us'

  const {
    initialCountry: initialCountryOption = userCountry,
    separateDialCode = true,
    showFlags = true,
    numberDisplayFormat = 'E164', // 'INTERNATIONAL',
    countryOrder = [...new Set([userCountry, 'fr', 'ci', 'nl'])],
    countrySelectorMode = null,
    countryNameLocale = navigator.language || 'en',
    // Labels for the mobile/small-screen country picker popup. Hand the caller
    // localized strings (e.g. via i18n) or fall back to English defaults.
    countryPicker = {},
  } = options
  const pickerLabels = {
    title: 'Select country',
    searchPlaceholder: 'Search',
    close: 'Close',
    noResults: 'No results found',
    ...countryPicker,
  }

  // Mutable so a re-init (e.g. after crossing the fullscreen breakpoint) can
  // keep the country the user picked instead of falling back to the default.
  let initialCountry = initialCountryOption
  const setInitialCountry = (iso2) => {
    const valid = validIso2(iso2)
    if (valid) initialCountry = valid
  }

  const phoneData = reactive({
    number: '', // E.164 formatted
    nationalNumber: '', // Formatted locally
    countryCode: '', // Dial code (+1)
    isoCode: '', // US, GB
    countryName: '',
    isValid: false,
  })

  let iti = null
  let currentEl = null
  let utilsPromise = null
  let widthObserver = null
  let trigger = null
  let detachContactButton = null

  const handleTriggerClick = () => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(matchDropdownWidthToField)
    } else {
      matchDropdownWidthToField()
    }
  }

  // Fill the field (and the rest of the form, when it has matching inputs)
  // from a contact on the device. Only offered where contacts are reachable.
  const handleContactClick = async () => {
    if (!currentEl) return
    try {
      const result = await fillFromContacts({ input: currentEl, iti })
      if (result) updatePhoneData()
    } catch (error) {
      console.error('[useTelephone] filling from contacts failed', error)
    }
  }
  const loadUtils = () => (utilsPromise ??= import('intl-tel-input/utils'))

  // intl-tel-input v18+ loads utils asynchronously: getNumber()/isValidNumber()
  // throw until intlTelInput.utils is populated, so guard every update.
  const updatePhoneData = () => {
    if (!iti || !intlTelInput.utils) return

    phoneData.number = iti.getNumber() || '' // E.164 by default in v18+
    phoneData.nationalNumber = iti.getNumber('NATIONAL') || ''

    // v29: getSelectedCountry() (renamed from getSelectedCountryData) can be null
    const countryData = iti.getSelectedCountry()
    phoneData.countryCode = countryData?.dialCode ? `+${countryData.dialCode}` : ''
    phoneData.isoCode = countryData?.iso2 || ''
    phoneData.countryName = countryData?.name || ''
    phoneData.isValid = iti.isValidNumber()
  }

  // Utility functions for validation
  const hasTel = (phone) => {
    // Basic regex or phone number validation logic
    return !!phone && phone.length > 5
  }

  const hasEmail = (email) => {
    // Basic email validation
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  // Accept a native <input>, a wrapper element, or a Vue component
  // (e.g. f7-list-input, whose $el is the <li> wrapping the input)
  const resolveInput = (raw) => {
    if (!raw) return null
    if (raw instanceof HTMLInputElement) return raw
    const root = raw.$el || raw
    return root.querySelector?.('input') || null
  }

  // intl-tel-input renders the selector in one of two ways:
  //   DROPDOWN -> popover card anchored to the field (desktop / large screens)
  //   FULLSCREEN -> full-screen sheet popup (mobile / small screens)
  // We always use DROPDOWN so the country list DOM is built and detached; on
  // small screens the built-in popover/full-screen sheet is suppressed and a
  // Framework7 popup (fullscreen on mobile) is opened instead, keeping the
  // picker consistent with the rest of the app.
  const POPOVER_MIN_WIDTH = 768
  const resolveCountrySelectorMode = () => 'DROPDOWN'
  const isMobileView = () =>
    typeof window !== 'undefined' && window.innerWidth < POPOVER_MIN_WIDTH

  // Country picker popup (opened on small screens instead of intl-tel-input's
  // built-in FULLSCREEN sheet). The list rows are snapshotted from the
  // library's own country data (flags, names and ordering stay in sync with
  // the instance) and rendered by the shared CountryPicker popup.
  const openCountryPopup = () => {
    if (!currentEl) return
    if (currentEl.disabled || currentEl.readOnly) return

    // Ensure intl-tel-input has built its country list. The library lazily
    // creates the dropdown DOM on first open; briefly open/close its dropdown
    // (which is rendered detached at body level) to force the list to exist.
    try { iti?.openDropdown?.() } catch {}
    try { iti?.closeDropdown?.() } catch {}

    const id = currentEl?.dataset?.intlTelInputId
    const itiList = id
      ? document.getElementById(`iti-${id}__country-listbox`)
      : null
    const source = itiList || document.querySelector('.iti__country-list')

    // Extract rows from the DOM if available; otherwise fall back to the
    // library's internal country data so the list is never empty.
    let rows = []
    if (source) {
      source.querySelectorAll('li[data-iso2]').forEach((li) => {
        rows.push({
          iso2: li.dataset.iso2,
          dialCode: li.dataset.dialCode,
          name: (li.querySelector('.iti__country-name')?.textContent || '').trim(),
          html: li.innerHTML,
        })
      })
    }
    if (rows.length === 0 && iti) {
      // Fallback: build rows from the library's internal country data.
      const countries = iti.countries || iti.getCountries?.() || []
      rows = countries.map((c) => ({
        iso2: c.iso2?.toLowerCase(),
        dialCode: c.dialCode,
        name: c.name,
        html: `<span class="iti__flag iti__${c.iso2?.toLowerCase()}"></span><span class="iti__country-name">${c.name}</span>`,
      })).filter((r) => r.iso2)
    }
    if (!rows.length) return

    useCountryPicker().open({
      title: pickerLabels.title,
      searchPlaceholder: pickerLabels.searchPlaceholder,
      closeLabel: pickerLabels.close,
      noResultsLabel: pickerLabels.noResults,
      rows,
      onSelect: (iso2) => {
        try {
          // Dispatches 'countrychange' and 'input' so phoneData stays in sync.
          iti.setSelectedCountry(iso2)
        } catch (error) {
          console.error('[useTelephone] selecting country failed', iso2, error)
        }
      },
    })
  }

  // Runs during the capture phase, before intl-tel-input's own click handler,
  // so the built-in dropdown never opens on small screens.
  const handleCountryTriggerCapture = (event) => {
    if (!isMobileView()) return
    event.preventDefault()
    event.stopImmediatePropagation()
    openCountryPopup()
  }

  // intl-tel-input measures the input during init and caches that width on the
  // country selector (style.width), then never re-measures. Framework7 lays the
  // field out after init, so the popover ends up narrower than the field.
  // Re-applying the field's real width once the selector is open fixes it.
  const matchDropdownWidthToField = () => {
    if (!currentEl || typeof document === 'undefined') return
    const panel = document.querySelector(
      '.iti--detached-country-selector .iti__country-selector:not(.iti__hide)',
    )
    if (!panel) return
    const width = Math.round(currentEl.getBoundingClientRect().width)
    if (width > 0) panel.style.width = `${width}px`
  }

  const observeInputWidth = () => {
    if (typeof ResizeObserver === 'undefined' || !currentEl) return
    widthObserver = new ResizeObserver(() => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(matchDropdownWidthToField)
      } else {
        matchDropdownWidthToField()
      }
    })
    widthObserver.observe(currentEl)
  }

  const init = (el) => {
    if (!el || el === currentEl) return
    destroy()
    currentEl = el

    iti = intlTelInput(el, {
      separateDialCode,
      initialCountry,
      countryOrder,
      countrySelectorMode: countrySelectorMode || resolveCountrySelectorMode(),
      countryNameLocale,
      showFlags,
      numberDisplayFormat,
      // Render the dropdown at body level: F7 gives each list item
      // `position: relative`, so otherwise the neighbouring <li> elements
      // paint over the open dropdown.
      dropdownParent: typeof document !== "undefined" ? document.body : undefined,
      loadUtils,
    })

    el.addEventListener('countrychange', updatePhoneData)
    el.addEventListener('input', updatePhoneData)
    el.addEventListener('blur', updatePhoneData)

    // The library opens the country selector in its own click handler on the
    // flag button, so a listener added here runs right after it and can size
    // the popover to the field.
    trigger = el.parentElement?.querySelector('.iti__selected-country') || null
    trigger?.addEventListener('click', handleTriggerClick)
    trigger?.addEventListener('click', handleCountryTriggerCapture, true)

    // Contact shortcut at the end of the field (device contacts only).
    detachContactButton = attachContactButton(el, handleContactClick)

    // Track the field width so the popover is sized to the real field.
    observeInputWidth()

    // Populate phoneData once the async utils module is ready
    loadUtils().then(() => updatePhoneData()).catch(() => {})
    updatePhoneData()
  }

  const destroy = () => {
    if (widthObserver) {
      widthObserver.disconnect()
      widthObserver = null
    }
    detachContactButton?.()
    detachContactButton = null
    useCountryPicker().close()
    trigger?.removeEventListener('click', handleTriggerClick)
    trigger?.removeEventListener('click', handleCountryTriggerCapture, true)
    trigger = null
    if (currentEl) {
      currentEl.removeEventListener('countrychange', updatePhoneData)
      currentEl.removeEventListener('input', updatePhoneData)
      currentEl.removeEventListener('blur', updatePhoneData)
      currentEl = null
    }
    if (iti) {
      iti.destroy()
      iti = null
    }
  }

  let resizeHandler = null
  let lastMode = null

  const handleResize = () => {
    if (countrySelectorMode || !currentEl) return
    const nextMode = resolveCountrySelectorMode()
    if (nextMode === lastMode) {
      // Same mode, but the field width may have changed (window resize).
      matchDropdownWidthToField()
      return
    }
    lastMode = nextMode
    const el = currentEl
    // Preserve what the user already typed across the re-init, together with
    // the country they picked (re-initing would fall back to initialCountry).
    const value = el.value
    const caret = el.selectionStart
    const selectedCountry = iti?.getSelectedCountry()?.iso2 || null
    destroy()
    if (selectedCountry) setInitialCountry(selectedCountry)
    init(el)
    if (value) {
      el.value = value
      try {
        el.setSelectionRange(caret, caret)
      } catch {
        /* input type does not support selection */
      }
      updatePhoneData()
    }
  }

  onMounted(() => {
    const raw = isRef(inputElement) ? inputElement.value : inputElement
    init(resolveInput(raw))
    lastMode = resolveCountrySelectorMode()
    if (typeof window !== 'undefined') {
      resizeHandler = handleResize
      window.addEventListener('resize', resizeHandler, { passive: true })
    }
  })

  // Inputs behind v-if render after mount; (re)init when the ref appears and
  // tear down when it disappears (several tel inputs can share a page).
  if (isRef(inputElement)) {
    watch(inputElement, (raw) => {
      if (!raw) {
        destroy()
        return
      }
      init(resolveInput(raw))
    })
  }

  onUnmounted(() => {
    if (resizeHandler && typeof window !== 'undefined') {
      window.removeEventListener('resize', resizeHandler)
      resizeHandler = null
    }
    destroy()
  })

  return {
    phoneData,
    instance: () => iti,
    hasTel,
    hasEmail,
    contactsAvailable,
    pickAndFillContact: handleContactClick,
  }
}
